import { DynamoDbPolicy, DynamoDbPolicyDetails, DynamoDbMortgageDetails } from '@ourbranch/schema-types/dynamodb';
import cloneDeep from 'lodash.clonedeep';
import dayjs from 'dayjs';
import { type ArrayReadStrategy, getArrayMergePolicy } from './segment-merge/array-merge-policy';
import { clearsLegacyFo6RoofType, isRemovableRiskScalarField } from './segment-merge/removable-scalar-policy';
import { isWholeValueField } from './segment-merge/whole-value-policy';

type makePolicyDetailsFromSegmentsOptions = {
  activeSegmentsOnly?: boolean;
  activeOnDate?: string;
  ignoreRenewalSegments?: boolean;
  upToSegmentIndex?: number;
  upToSegmentId?: string;
  keepPremium?: boolean;
  /**
   * Read a field with a different strategy than the registry declares. Diagnostics only: the
   * #37140 impact scan replays a policy under the pre-#37140 read to list what the migration moves.
   * Production reads must not pass it.
   */
  arrayReadStrategyOverrides?: Record<string, ArrayReadStrategy>;
};

/**
 * Returns the single primary mortgage for a farm policy, or `undefined` if it has none.
 *
 * Farm mortgages live per-structure on `farmLocations[].risks[].building.mortgageDetails`
 * (#32526); the legacy flat `farm.mortgageDetails` is deprecated and cleared on save. There is
 * exactly one `primary: true` across the whole policy (staff-fe enforces a single primary via a
 * radio), but it can sit on ANY building — so gather every building's mortgages across the merged
 * policy details, then pick the primary, falling back to the first (mirroring the home/condo
 * pattern in underwriting-rules' property-base-post-data-rules).
 *
 * Accepts either a raw farm policy (with `segments` — merged here via
 * `makePolicyDetailsFromSegments`) or an already-merged policy-details object (with top-level
 * `farmLocations`). It never reads a single `segments[0]` directly — always the merged view.
 */
export function getPrimaryFarmMortgage(
  policyOrDetails: Record<string, any> | null | undefined
): DynamoDbMortgageDetails | undefined {
  if (!policyOrDetails) {
    return undefined;
  }

  const details =
    Array.isArray(policyOrDetails.segments) && policyOrDetails.segments.length > 0
      ? makePolicyDetailsFromSegments(policyOrDetails as DynamoDbPolicy, policyOrDetails.segments)
      : (policyOrDetails as DynamoDbPolicyDetails);

  const mortgages: DynamoDbMortgageDetails[] = ((details?.farmLocations as any[]) || []).flatMap((location: any) =>
    (location?.risks || []).flatMap((risk: any) => risk?.building?.mortgageDetails || [])
  );

  return mortgages.find((mortgage) => mortgage?.primary) || mortgages[0];
}

/**
 * Reconstructs full policy details from farm policy segments using delta-based approach
 * For farm policies:
 * - Segment 0: Contains full policy details (base)
 * - Segments 1+: Contains only delta changes (except renewal segments which are ignored)
 * @param policy The farm policy
 * @param segments Array of policy segments
 * @param upToSegmentIndex Optional index to stop at (for partial reconstruction)
 * @returns Complete policy details with all segments applied
 */
export function makePolicyDetailsFromSegments(
  policy: DynamoDbPolicy,
  segments: DynamoDbPolicyDetails[],
  options?: makePolicyDetailsFromSegmentsOptions
): DynamoDbPolicyDetails {
  const {
    activeSegmentsOnly = false,
    activeOnDate = '',
    ignoreRenewalSegments = true,
    upToSegmentIndex = undefined,
    upToSegmentId = undefined,
    keepPremium = false,
    arrayReadStrategyOverrides
  } = options || {};

  if (!segments || segments.length === 0) {
    throw new Error('Cannot make policy details from empty segments array');
  }

  // Resolve the stop index once, up front. When the caller targets the inception
  // segment (upToSegmentId matching segments[0]), stopIndex is 0 and no deltas
  // get applied — preserving the inception state.
  let stopIndex = segments.length - 1;
  if (upToSegmentId !== undefined) {
    const targetIndex = segments.findIndex((s) => s.segmentId === upToSegmentId);
    if (targetIndex >= 0) {
      stopIndex = targetIndex;
    }
  }
  if (upToSegmentIndex !== undefined) {
    stopIndex = Math.min(stopIndex, upToSegmentIndex);
  }

  // Start with the base segment (segment 0) - always contains full data
  const fullSegmentDetails: DynamoDbPolicyDetails = cloneDeep(segments[0]);

  // Apply delta segments in sequence (segments 1+ up to and including stopIndex)
  for (let i = 1; i <= stopIndex; i += 1) {
    if (activeSegmentsOnly && dayjs(segments[i].startDate).isAfter(dayjs())) {
      // Ignore any segments that start in the future if `activeSegmentsOnly` is true
      continue;
    }

    if (activeOnDate && dayjs(segments[i].startDate).isAfter(dayjs(activeOnDate))) {
      // for claims only show policy segments that are active on date of loss
      continue;
    }

    if (ignoreRenewalSegments && dayjs(segments[i].startDate).isAfter(policy.endDate)) {
      // ignore renewal segments
      continue;
    }

    // Apply the delta segment to the full segment details
    applyFarmObjectDelta(fullSegmentDetails, segments[i], arrayReadStrategyOverrides);
  }

  if (fullSegmentDetails.premium && !keepPremium) {
    // we don't want the segment details premium to override policy.premium
    // and we copy the elements here to the root of policy
    delete (fullSegmentDetails as any).premium;
  }

  return fullSegmentDetails;
}

/**
 * Generic function to apply delta changes to an array of objects
 * @param baseArray The base array to apply changes to
 * @param deltaArray The array containing delta changes
 * @param idField The field name used to identify objects
 */
function applyArrayDelta(
  baseArray: any[],
  deltaArray: any[],
  idField: string,
  readOverrides: ReadStrategyOverrides
): void {
  for (const deltaItem of deltaArray) {
    const itemId = deltaItem[idField];
    const existingItemIndex = baseArray.findIndex((item: any) => item[idField] === itemId);

    if (existingItemIndex >= 0) {
      if (deltaItem.isDeleted) {
        // Remove deleted items
        baseArray.splice(existingItemIndex, 1);
      } else {
        // Update existing item with delta changes
        applyFarmObjectDelta(baseArray[existingItemIndex], deltaItem, readOverrides);
      }
    } else if (!deltaItem.isDeleted) {
      // An unmatched tombstone has nothing to remove; pushing it materialised a phantom (#34355).
      baseArray.push(cloneDeep(deltaItem));
    }
  }
}

/** A mergeable base for an object delta: not null, not an array, not a Date, not a primitive. */
function isPlainObject(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date);
}

type ReadStrategyOverrides = Record<string, ArrayReadStrategy> | undefined;

/**
 * Whether a stored delta array has the shape only a whole-list write produces (#37140).
 *
 * `scheduleCoverages` / `coverageF` are written whole since #31867 (2026-06-15); before that they
 * were written as a positional diff. Segments are historical records and carry no write-era marker,
 * so the shape is the evidence. Each rule below rejects a shape the positional diff produced:
 *
 *   1. An `isDeleted` entry, or an `isDeleted` object in an entry's nested list — the old diff's
 *      tombstones for a removed coverage or item (`{ id: undefined, isDeleted: true }`, item
 *      tombstones inside `items`). A whole list never carries one.
 *   2. An entry missing a required field — the old diff stored a modified entry as only its changed
 *      fields (`{ items: [...] }` without `coverageType`; a coverageF item as `{ limit, id }`).
 *
 * A complete list that shares no key with a non-empty base is still read as the whole list. The old
 * diff could also store an ADDITION as just the appended entries (`[INCOME]` onto `[SPP]`), which
 * this reads as "SPP removed". That era is closed — nothing writes a positional diff any more — and
 * the #37140 scan read every stored farm segment in CFM and Forreston production: no such append
 * exists there. The one found anywhere (Forreston dev 680109206-farm-001-FM) was already mis-read
 * before #37140. Guarding against it would instead leave a real "replace every item" change unread,
 * re-typing removed SPP items as the new coverage type (devlo review on #37142).
 *
 * An empty list is read as a whole-list write ("remove them all"). The old diff stored a removal as
 * tombstones; it could store `[]` only when a list differed in nothing but key order, meaning "no
 * change". The #37140 scan found no stored `[]` on a non-empty base in CFM, Forreston or Oakwood
 * (dev or production), and every write since #31867 is a whole list, so `[]` is never that no-op.
 */
export function isFullArrayDelta(
  deltaArray: any[],
  { requiredFields }: { requiredFields: readonly string[] }
): boolean {
  if (deltaArray.length === 0) {
    return true;
  }

  const isPresent = (value: unknown) => value !== null && value !== undefined && value !== '';
  const everyEntryComplete = deltaArray.every(
    (entry) =>
      isPlainObject(entry) &&
      entry.isDeleted !== true &&
      requiredFields.every((field) => isPresent(entry[field])) &&
      Object.values(entry).every(
        (value) => !Array.isArray(value) || !value.some((nested) => isPlainObject(nested) && nested.isDeleted === true)
      )
  );
  return everyEntryComplete;
}

/**
 * Generic function to apply delta changes to an object
 * @param baseObject The base object to apply changes to
 * @param deltaObject The object containing delta changes
 */
function applyFarmObjectDelta(baseObject: any, deltaObject: any, readOverrides?: ReadStrategyOverrides): void {
  // #37180: decided on the base as it was before this delta (the loop may set roofShape first). Only an FO-6
  // structure stored the old way qualifies; a dwelling's roofShape never leaves its roofType blank-shaped.
  const clearsRoofType = clearsLegacyFo6RoofType(baseObject, deltaObject);
  for (const key in deltaObject) {
    if (key === 'isDeleted') {
      // Skip the isDeleted flag itself
      continue;
    }

    const deltaValue = deltaObject[key];

    if (deltaValue === null) {
      // #37180: an FO-6 roof shape moved out of roofType (roofShape set alongside) clears roofType.
      if (key === 'roofType' && clearsRoofType) {
        baseObject[key] = null;
        continue;
      }
      // A declared removable scalar is the one field class whose explicit null means "the agent
      // removed this coverage" rather than "the payload didn't carry this field" (#34975); every
      // other null is the latter, and unsetting it here would rewrite in-force policies.
      if (isRemovableRiskScalarField(key)) {
        baseObject[key] = null;
      }
      continue;
    }

    if (deltaValue === undefined) {
      // Skip undefined values
      continue;
    }

    if (isWholeValueField(key)) {
      baseObject[key] = cloneDeep(deltaValue);
      continue;
    }

    if (Array.isArray(deltaValue)) {
      const read = readOverrides?.[key] ?? getArrayMergePolicy(key).read;

      if (read.kind === 'replace' || (read.kind === 'replace-if-full-array' && isFullArrayDelta(deltaValue, read))) {
        baseObject[key] = cloneDeep(deltaValue);
        continue;
      }

      // Handle array fields recursively
      if (!baseObject[key]) {
        baseObject[key] = [];
      } else if (!Array.isArray(baseObject[key])) {
        // An array delta cannot be keyed into a non-array base; merging it threw
        // `t.findIndex is not a function`. The delta is the only value there is to keep.
        baseObject[key] = cloneDeep(deltaValue);
        continue;
      }

      // `replace-if-full-array` lands here for a historical positional diff, read as it always was.
      applyArrayDelta(baseObject[key], deltaValue, read.kind === 'key' ? read.key : 'id', readOverrides);
    } else if (typeof deltaValue === 'object' && !(deltaValue instanceof Date)) {
      // Handle nested object fields recursively. Same shape rule as the array branch above: an
      // array or scalar base cannot take object keys (assigning onto a primitive throws; onto an
      // array is silently lost by JSON), so the delta's keys land on a fresh object instead (#35385).
      if (!isPlainObject(baseObject[key])) {
        baseObject[key] = {};
      }
      applyFarmObjectDelta(baseObject[key], deltaValue, readOverrides);
    } else {
      // Simple field - just copy the value
      baseObject[key] = deltaValue;
    }
  }
}
